function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function walkStrings(value, out = []) {
  if (value == null) return out
  if (typeof value === 'string') {
    out.push(value)
    return out
  }
  if (Array.isArray(value)) {
    value.forEach((item) => walkStrings(item, out))
    return out
  }
  if (typeof value === 'object') Object.values(value).forEach((item) => walkStrings(item, out))
  return out
}

function executionText(dataJson) {
  const values = []
  ;(dataJson?.forms || []).forEach((form) => {
    ;(form?.remarks || []).forEach((field) => {
      if (field?.remark) values.push(field.remark)
      if (field?.remark_mask) values.push(field.remark_mask)
    })
  })
  walkStrings(dataJson?.postCommands, values)
  ;(dataJson?.actions || []).forEach((action) => walkStrings(action?.commands, values))
  return values.join('\n')
}

function reservationMaps(dataJson) {
  return (dataJson?.onLoad || [])
    .filter((step) => /sabre\/reservation/i.test(String(step?.api || '')))
    .flatMap((step) => (Array.isArray(step?.map) ? step.map : []))
}

function formFields(dataJson) {
  return (dataJson?.forms || []).flatMap((form) => Array.isArray(form?.remarks) ? form.remarks : [])
}

function fieldMatchesTag(field, tag) {
  const expected = normalizeText(tag)
  return [field?.tag, field?.campoForm, field?.id].some((value) => normalizeText(value) === expected)
}

function selectorRequested(text) {
  if (/\b(todos|todas|cada)\b[^.\n]{0,30}\bsegment/.test(text)) return 'all'
  if (/\bultimo\b[^.\n]{0,30}\bsegment|\bsegment[^.\n]{0,30}\bultimo\b/.test(text)) return 'last'
  if (/\bprimer\w*\b[^.\n]{0,30}\bsegment|\bsegment[^.\n]{0,30}\bprimer\w*\b/.test(text)) return 'first'
  return ''
}

function semanticMapMatches(map, source, field, selector) {
  const src = normalizeText(map?.source)
  const fld = normalizeText(map?.field).replace(/[_-]/g, '')
  const sel = normalizeText(map?.select || 'first')
  if (source === 'segments' && !/^segments?$/.test(src)) return false
  if (source === 'passengers' && !/^(passengers?|pax)$/.test(src)) return false
  if (field === 'number' && !/^(number|sequence|segmentnumber)$/.test(fld)) return false
  if (field === 'name' && !/^(name|full|fullname)$/.test(fld)) return false
  return !selector || sel === selector
}

function legacyMapMatches(map, source, field, selector) {
  const path = normalizeText(map?.path)
  if (!path) return false
  if (source === 'segments' && !/segments?.*segment/.test(path)) return false
  if (source === 'passengers' && !/passengers?.*passenger/.test(path)) return false
  if (field === 'number' && !/(sequence|segment_number|segmentnumber|\.id)$/.test(path)) return false
  // first_name o last_name por separado NO satisfacen "nombre del pasajero".
  // La lectura semantica field:name compone APELLIDO/NOMBRE de forma determinista.
  if (field === 'name' && !/(full_name|fullname|\.name)$/.test(path)) return false
  if (selector === 'all') return false
  if (selector === 'last') return false
  return true
}

function directPlaceholderMatches(text, source, field, selector) {
  const compact = normalizeText(text).replace(/[_-]/g, '')
  if (source === 'segments' && field === 'number') {
    if (selector === 'last') return /pnr\.lastsegment\.(number|sequence)/.test(compact)
    if (selector === 'all') return /pnr\.segments\.all\.(number|sequence)/.test(compact)
    return /pnr\.(firstsegment|segment\.1)\.(number|sequence)/.test(compact)
  }
  if (source === 'passengers' && field === 'name') {
    return /pnr\.passenger(s\.1)?\.(name|full)/.test(compact)
  }
  return false
}

function tagIsConsumed(map, commandsText) {
  const tag = String(map?.tag || '').trim()
  if (!tag) return false
  const escaped = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp('(\\[' + escaped + '\\]|\\{\\{' + escaped + '\\}\\})', 'i').test(commandsText)
}

function routeRequested(text) {
  return String(text || '')
    .split(/[\n.!?;]+/)
    .some((clauseValue) => {
      const clause = clauseValue.trim()
      if (!clause) return false
      const hasRouteWord = /\b(ruta|itinerario)\b/.test(clause)
      const hasOriginAndDestination = /origen[^\n]{0,45}destino|destino[^\n]{0,45}origen/.test(clause)
      if (hasOriginAndDestination) return true
      if (!hasRouteWord) return false

      // "remark de itinerario" clasifica el tipo de remark; no pide leer la ruta.
      const onlyRemarkClassification = /\b(remark|observacion)\b[^\n]{0,120}\b(de\s+)?itinerario\b/.test(clause) &&
        !/\b(mostrar|muestra|muestre|obtener|obtenga|leer|lea|cargar|cargue|traer|traiga|recuperar|recupere|visualizar|campo)\b/.test(clause)
      if (onlyRemarkClassification) return false

      // Fuera de la clasificación de un remark, una mención de ruta/itinerario
      // sigue siendo un requisito independiente de datos de reserva.
      return true
    })
}

export function reservationIntent(textValue) {
  const text = normalizeText(textValue)
  return {
    segmentNumber: /(numero|nro|num\.?)[^.\n]{0,18}\bsegmento|\bsegmento[^.\n]{0,18}(numero|nro|num\.?)/.test(text),
    passengerName: /nombre[^.\n]{0,30}\bpasajer|\bpasajer[^.\n]{0,30}nombre/.test(text),
    passengerSelect: /(select|lista|desplegable)[^.\n]{0,45}\bpasajer|\bpasajer[^.\n]{0,45}(select|lista|desplegable)/.test(text),
    autoSelectSingle: /(si|cuando)[^.\n]{0,35}(solo|un|uno|unico)[^.\n]{0,25}\bpasajer|\bun\s+solo\s+pasajer/.test(text),
    routeField: routeRequested(text),
    segmentSelector: selectorRequested(text),
    writesRemark: /\bremark|observacion/.test(text),
  }
}

export function reservationFixPrompt(diagnostics, dataJson, requestText) {
  const errors = (Array.isArray(diagnostics) ? diagnostics : []).filter((item) => item?.severity === 'error')
  if (!errors.length) return ''
  return (
    'CORRECCIÓN DE DATOS DE RESERVA REQUERIDA. Conservá todos los formularios, campos, comandos y reglas actuales. ' +
    'Agregá o corregí únicamente las lecturas de reserva explícitamente solicitadas; no agregues ruta, pasajeros, segmentos ni otros datos que no fueron pedidos. ' +
    'Para lecturas automáticas usá onLoad con api "sabre/reservation" y map semántico. Los campos que el usuario pidió mostrar deben ser visibles y readonly:true, y todo valor que deba enviarse tiene que consumirse en su remark/comando. ' +
    'Devolvé el data.json COMPLETO corregido.\n\n' +
    'ERRORES:\n' + errors.map((item, index) => (index + 1) + '. ' + item.message).join('\n') +
    '\n\nREQUISITOS CONFIRMADOS:\n' + String(requestText || '') +
    '\n\nDATA.JSON ACTUAL:\n' + JSON.stringify(dataJson)
  )
}

export function validateReservationRequirements(dataJson, requestText) {
  const intent = reservationIntent(requestText)
  const diagnostics = []
  const maps = reservationMaps(dataJson)
  const commands = executionText(dataJson)

  const checkRead = ({ enabled, source, field, selector, label }) => {
    if (!enabled) return
    const map = maps.find((entry) => semanticMapMatches(entry, source, field, selector) || legacyMapMatches(entry, source, field, selector))
    const direct = directPlaceholderMatches(commands, source, field, selector)
    if (!map && !direct) {
      diagnostics.push({
        code: 'RESERVATION_REQUIREMENT_MISSING',
        severity: 'error',
        message: 'La petición exige ' + label + ', pero el data.json no contiene una lectura compatible de la reserva.',
        raw: '',
      })
      return
    }
    if (intent.writesRemark && map && !tagIsConsumed(map, commands)) {
      diagnostics.push({
        code: 'RESERVATION_VALUE_UNUSED',
        severity: 'error',
        message: 'El dato automático "' + (map.tag || label) + '" se obtiene de la reserva, pero no se usa en el remark/comando final.',
        raw: String(map.tag || ''),
      })
    }
  }

  checkRead({
    enabled: intent.segmentNumber,
    source: 'segments',
    field: 'number',
    selector: intent.segmentSelector || 'first',
    label: (intent.segmentSelector === 'all' ? 'los números de todos los segmentos' : 'el número dinámico del segmento'),
  })
  checkRead({
    enabled: intent.passengerName,
    source: 'passengers',
    field: 'name',
    selector: intent.passengerSelect ? 'all' : 'first',
    label: intent.passengerSelect ? 'los nombres de los pasajeros' : 'el nombre del pasajero',
  })

  if (intent.passengerSelect) {
    const map = maps.find((entry) =>
      semanticMapMatches(entry, 'passengers', 'name', 'all') &&
      ['datasource', 'options'].includes(normalizeText(entry?.target)),
    )
    const field = map && formFields(dataJson).find((entry) =>
      fieldMatchesTag(entry, map.tag) && normalizeText(entry?.typedata) === 'select' && entry?.visible !== false,
    )
    if (!map || !field) {
      diagnostics.push({
        code: 'RESERVATION_PASSENGER_DATASOURCE_MISSING',
        severity: 'error',
        message: 'La petición exige un select alimentado con los pasajeros de la reserva, pero no existe un onLoad target:"datasource" ligado a un select visible.',
        raw: '',
      })
    } else if (intent.autoSelectSingle && map.autoSelectSingle === false) {
      diagnostics.push({
        code: 'RESERVATION_PASSENGER_AUTOSELECT_MISSING',
        severity: 'error',
        message: 'El select de pasajeros desactiva la selección automática cuando existe una sola opción.',
        raw: String(map.tag || ''),
      })
    }
  }

  if (intent.routeField) {
    const routeMap = maps.find((entry) => {
      const source = normalizeText(entry?.source)
      const template = normalizeText(entry?.template)
      return /^segments?$/.test(source) && template.includes('{origin}') && template.includes('{destination}')
    })
    const routeField = routeMap && formFields(dataJson).find((entry) =>
      fieldMatchesTag(entry, routeMap.tag) && entry?.visible !== false,
    )
    if (!routeMap || !routeField) {
      diagnostics.push({
        code: 'RESERVATION_ROUTE_FIELD_MISSING',
        severity: 'error',
        message: 'La petición exige mostrar la ruta, pero no existe un campo visible ligado al origen y destino del segmento de la reserva.',
        raw: '',
      })
    }
  }

  return diagnostics
}

export function reservationGenerationContract() {
  return [
    'CONTRATO OBLIGATORIO PARA DATOS DE RESERVA:',
    '- Nunca conviertas "primer segmento" al literal 1. Debes leer el primer elemento actual y usar su number/sequence real.',
    '- Distingue number (numero de segmento), flight (numero de vuelo), count (cantidad) e index (posicion).',
    '- Para lecturas automaticas usa onLoad con api "sabre/reservation" y map semantico.',
    '- Cada map semantico usa: tag, source (segments|passengers), select (first|last|index|all), field y, para index, index base 1.',
    '- Si se pide el nombre del primer pasajero usa source:"passengers", select:"first", field:"name". Ligalo a un field visible readonly:true con el mismo tag y, si debe guardarse, consume ese tag en el remark/comando.',
    '- Para todos los valores en un solo campo usa select:"all", aggregate:"join" y separator.',
    '- Si el usuario pide un select/lista de datos de la reserva, liga el select mediante onLoad map con target:"datasource". Usa source, select:"all", field, valueField/textField cuando difieran y autoSelectSingle:true. El field debe ser typedata:"select", visible y puede mantener datasource:[] porque el runtime lo hidrata al abrir.',
    '- Para mostrar un dato derivado usa un field visible readonly:true y un map al mismo tag. Puedes componerlo con template, por ejemplo source:"segments", select:"first", filter:{type:"air"}, template:"{origin}-{destination}". No lo conviertas en emisor oculto si el usuario pidió verlo.',
    '- Para ejecutar un comando por elemento usa postCommands: [{"loop":{"source":"segments","as":"segment","commands":["5H-SEG [segment.number]"]}}].',
    '- Campos obtenidos de la reserva son automaticos: no pidas al usuario capturarlos. Deben consumirse en el remark/comando solicitado.',
    '- "sin limite de texto" significa no generar maxlength/maxLength.',
    '- No agregues ER/ET/IR ni pantallas extra si el usuario no los solicito; conserva las reglas transaccionales indicadas por el pedido.',
    'Ejemplo select dinámico: {"onLoad":[{"api":"sabre/reservation","map":[{"tag":"pasajero","source":"passengers","select":"all","field":"name","target":"datasource","autoSelectSingle":true},{"tag":"ruta","source":"segments","select":"first","filter":{"type":"air"},"template":"{origin}-{destination}"}]}]}',
  ].join('\n')
}

export function reservationInterpretationGuide() {
  return (
    'Al interpretar datos de una reserva, no sustituyas valores dinámicos por literales. ' +
    'Distingue número de segmento, número de vuelo y cantidad de segmentos; identifica si el usuario pide primero, último, una posición concreta o todos. ' +
    'El nombre del pasajero y los datos del segmento son lecturas automáticas de la reserva, no campos que el usuario deba capturar.'
  )
}
